import { action, autorun, makeObservable, observable, toJS } from "mobx";
import { message } from "antd";
import {
	RULES_FILE_LOAD_FILE_BY_NAME,
	RULES_FILE_VALIDATE_QUOTE,
} from "../../configs/api-paths";
import BaseInsuranceCalculatorService from "../base/BaseInsuranceCalculatorService";
import { fetchProtectedResource } from "../../utilities";

const LOCAL_STORAGE_NAMESPACE = "form-data";

/**
 * Service dealing with the creation of quotes for clients (Quoting).
 */
class InsuranceRateQuotingService extends BaseInsuranceCalculatorService {
	/**
	 * Information about the current client returned from server with additional insurer information.
	 * @type {[]}
	 */
	@observable
	insuranceQuote = [];

	@observable
	rateFileSelected;

	@observable
	loadedRateFile;

	constructor(context) {
		super(context);

		autorun(() => {
			if (this.context.applicationService.settings.sessionPersistence) {
				this.context.localStorageService.load(
					LOCAL_STORAGE_NAMESPACE,
					"clientData",
					this.setClientData
				);
			} else {
				this.setClientData({});
			}
		});
		makeObservable(this);
	}

	@action
	setInsuranceQuote = (data) => {
		this.insuranceQuote = data;
	};

	@action
	setRateFileSelected = (data) => {
		this.rateFileSelected = data;
	};

	@action
	setLoadedRateFile = (date) => {
		this.loadedRateFile = date;
	};

	loadRateFile = async (fileName) => {
		if (this.loadedRateFile === fileName) return; // file is already loaded

		let hideLoadingMessage = message.loading("Loading...", 0);

		try {
			let url = RULES_FILE_LOAD_FILE_BY_NAME.replace("{fileName}", fileName);
			await fetchProtectedResource(url); // GET
			this.setLoadedRateFile(fileName);
		} catch (error) {
			message.error(
				`There was error while trying to load rate file. Error: ${error}`
			);
		} finally {
			hideLoadingMessage();
		}
	};

	/**
	 * Called when the user changes data. Sets it here and persists to browser storage.
	 * @param key String - name of the field
	 * @param val String - the data
	 */
	changeClientData = (key, val) => {
		super.changeClientData(key, val);

		if (this.context.applicationService.settings.sessionPersistence) {
			this.context.localStorageService.save(
				"clientData",
				toJS(this.clientData)
			);
		}
	};

	/**
	 * Sends client data to the server to run business logic on it and get insurance data for validation purposes.
	 * @returns {Promise<void>}
	 */
	calculateQuoteForValidation = async () => {
		let hideLoadingMessage = message.loading("Loading...", 0);

		const propsToSend = [
			"organisation",
			"organisation_name",
			"organisation_type",
			"startDate",
			"pl_turnover",
			"el",
			"el_wageroll",
			"el_volunteer_staff",
			"equipment",
			"equipment_cover_amt",
			"uk_only",
			"ngb_qualified",
			"appropriate_training",
			"hazardous_materials",
			"risk_assessment",
			"participation_form",
			"safety_briefing",
			"correct_operation",
			"previous_claims",
			"insurance_declaration",
			"activityTrades",
		];

		try {
			const insurerId = this.rateFileSelected.insurerId;
			const url = RULES_FILE_VALIDATE_QUOTE.replace("{insurerId}", insurerId);
			const result = await super.calculateRequest(url, propsToSend);

			hideLoadingMessage();
			this.setInsuranceQuote(result);
		} catch (error) {
			this.setInsuranceQuote(undefined);

			hideLoadingMessage();
			message.error(
				`There was error while trying to send data to the server. Error: ${error}`
			);
		}
	};
}

export default InsuranceRateQuotingService;
